import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DeliverTxResponse } from '@cosmjs/stargate'
import { assertIsDeliverTxSuccess, broadcastTx, traceIbcStatus2 } from '@/wallet-utils'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { queryKeys } from '@/query-keys'
import { fatal } from '@/utils'
import { IBCTransferStatus } from '@/wallet-utils'
import { MutationParameters } from './types'
import { useStrideChain } from '@/contexts/wallet'
import { flushSync } from 'react-dom'

export interface BroadcastWithdrawStTokenMutationReturnType {
  status: IBCTransferStatus
  transaction: DeliverTxResponse
}

const useBroadcastWithdrawTokenizedSharesMutation = ({
  withdrawStTokenRaw,
  setWithdrawStTokenTransaction
}: MutationParameters) => {
  const client = useQueryClient()

  const { getSigningStargateClient } = useStrideChain()

  const handleMutation = async (): Promise<BroadcastWithdrawStTokenMutationReturnType> => {
    if (!withdrawStTokenRaw) {
      throw fatal('Unable to broadcast empty signed transaction.')
    }

    const client = await getSigningStargateClient()

    const bytes = TxRaw.encode(withdrawStTokenRaw).finish()

    const transaction = await broadcastTx(client, bytes)

    assertIsDeliverTxSuccess(transaction)

    const status = await traceIbcStatus2({ denom: 'STRD', tx: transaction })

    // @TODO: Handle if ibc status === 'timeout'. We likely need a flow for this.
    return { status, transaction }
  }

  const handleSuccess = async ({ transaction }: BroadcastWithdrawStTokenMutationReturnType) => {
    flushSync(() => {
      setWithdrawStTokenTransaction(transaction)
    })

    await client.invalidateQueries({ queryKey: queryKeys.chainBalancesBase })

    await client.invalidateQueries({ queryKey: queryKeys.transactionHistoryLsmIbcBalancesBase })
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useBroadcastWithdrawTokenizedSharesMutation }
